/**
 * useUpcomingOpenHouseCount — count of upcoming open houses Shayaan is
 * actively hosting.
 *
 * Drives two pieces of UI:
 *  - The "Open Houses" link in the global header (hidden when count === 0)
 *  - The `/open-house` landing page (empty state when count === 0)
 *
 * The backend endpoint is `/api/open-houses/upcoming`. If it's missing
 * or fails for any reason we treat that as "0 hosted right now" — a
 * 404 isn't worth a red error card to the visitor, and the link should
 * silently disappear until there's something to advertise.
 *
 * Result is cached in sessionStorage so the link doesn't flicker on
 * client-side route changes.
 */
import { useEffect, useState } from 'react';
import { apiClient } from '@/shared/api';

const CACHE_KEY = 'openHouseCount.v1';

function readCache(): number {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (raw === null) return 0;
        const n = Number(raw);
        return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch {
        return 0;
    }
}

function writeCache(n: number): void {
    try {
        sessionStorage.setItem(CACHE_KEY, String(n));
    } catch {
        /* private mode / quota — non-fatal */
    }
}

/**
 * Drop the cached count so the next mount re-fetches. Called by the
 * admin create / cancel / close flows, which change what the public
 * header link should advertise.
 */
export function invalidateUpcomingOpenHouseCount(): void {
    try {
        sessionStorage.removeItem(CACHE_KEY);
    } catch {
        /* private mode / quota — non-fatal */
    }
}

export function useUpcomingOpenHouseCount(): number {
    const [count, setCount] = useState<number>(readCache);

    useEffect(() => {
        let cancelled = false;
        apiClient
            .get<{ success?: boolean; data?: unknown[] }>('/open-houses/upcoming')
            .then((resp) => {
                if (cancelled) return;
                const data = resp.data?.data;
                const n = Array.isArray(data) ? data.length : 0;
                setCount(n);
                writeCache(n);
            })
            .catch(() => {
                if (cancelled) return;
                setCount(0);
                writeCache(0);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return count;
}
